import * as Sentry from '@sentry/react'
import { useAmplitudeInit } from 'amplitude/useAmplitudeInit'
import { useSetAmplitudeIdentity } from 'amplitude/useSetAmplitudeIdentity'
import { useSupplierReferralLink } from 'amplitude/useSupplierReferralLink'
import { useTrackPartnerTags } from 'amplitude/useTrackPartnerTags'
import useBrandingMetaData from 'branding/useBrandingMetaData'
import { installReduxSelectorWarningForwarder } from 'debug/installReduxSelectorWarningForwarder'
import { setStudioMode as setStudioModeAction, StudioModeState } from 'ducks/studioMode'
import { useSentryReduxInfo } from 'errors/sentry/useSentryReduxInfo'
import { useAdaActive } from 'layout/widgets/adaChat/useAdaActive'
import SocketInitForChatDrawer from 'persistentContent/elements/SocketInitForChatDrawer'
import CustomImageryDialog from 'projectSections/elements/CustomImageryDialog'
import UnsavedDataRecoveryPrompt from 'projectSections/elements/UnsavedDataRecoveryPrompt'
import { AutoDesignInProgressDialog } from 'projectSections/sections/design/popups/AutoDesignInProgressDialog'
import { lazy, Suspense, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { SdkMounterIfNested } from 'sdk/SdkMounter'
import { useLocalStoragePoller } from 'storage/useLocalStoragePoller'
import { useSessionStorageLogic } from 'storage/useSessionStorageLogic'
import { useFeatureFlagDynamic } from 'util/featureFlags'
import { useInjectAdditionalFetchMiddleware } from 'util/fetch/request_middleware/fetchRequestMiddleware'
import ChatDrawer from '../layout/widgets/ChatDrawer'
import HelpCenterDrawer from '../layout/widgets/HelpCenterDrawer'
import myApiRestClient from '../restClient'
import AppStateManager from '../voice/AppStateManager'
import AccessDialog from './elements/AccessDialog'
import DeleteConfirmationDialog from './elements/DeleteConfirmationDialog'
import DesignerComponent from './elements/DesignerComponent'
import FloatingAdaIcon from './elements/FloatingAdaIcon'
import InvalidProjectDialog from './elements/InvalidProjectDialog'
import LoginWithNearmapDialog from './elements/LoginWithNearmapDialog'
import OrderComponentsDialog from './elements/OrderComponentsDialog'
import PvsellProposalDialog from './elements/PvsellProposalDialog'
import SwitchInterfaceConfirmationDialog from './elements/SwitchInterfaceConfirmationDialog'
import UploadLogoDialog from './elements/UploadLogoDialog'
import { FeaturesDialog } from './features/FeaturesDialog'
import { ListBulkEditGlobalHost } from './ListBulkEditGlobalHost'
import { ProjectPersistentContent } from './ProjectPersistentContent'
import { PromoDialogManager } from './promoDialog/PromoDialogManager'
import { useReleasePromos } from './promoDialog/useReleasePromos'
import { useUserAgreementPromo } from './promoDialog/useUserAgreementPromo'
import { ToastNotificationManager } from './toastNotification/ToastNotificationManager'
import FujiTourController from './tours/fuji'
import { useTourAutoShow } from './tours/hooks/useTourAutoShow'
import { useTourQuerySync } from './tours/hooks/useTourQuerySync'
import { usePrewarmStudioResources } from './usePrewarmStudioResources'
import InitSellerIntegrations from './utils/InitSellerIntegrations'
const SidePanel = lazy(() => import('../pages/home/SidePanel'))

const API_URL = window.API_ROOT + '/api'

export const PersistentContent = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [studioMode, setStudioMode] = useState<StudioModeState>('hidden')

  // Business Logic
  useSessionStorageLogic()
  useLocalStoragePoller()
  useAmplitudeInit()
  useSetAmplitudeIdentity()
  useTrackPartnerTags()
  useSupplierReferralLink()
  useBrandingMetaData()
  useInjectAdditionalFetchMiddleware()
  useTourQuerySync()
  useTourAutoShow()
  useSentryReduxInfo()
  usePrewarmStudioResources()

  // Promo Dialog spawners
  useUserAgreementPromo()
  useReleasePromos()

  const { active: showChatDrawerButton } = useAdaActive()

  //TODO: remove this once studio can be directly imported. Currently this happens when viewing a google lead
  const studioLoaded = !!window.AccountHelper
  const isHomePage = location.pathname === '/' || location.pathname === '/home'

  // Set up window.isFeatureEnabled for Studio's legacy JavaScript code
  const isFeatureEnabledHook = useFeatureFlagDynamic()
  useEffect(() => {
    window.isFeatureEnabled = (featureFlagName: string, target_value?: string): boolean | undefined => {
      // Studio uses plain strings, so we accept string here for compatibility
      return isFeatureEnabledHook(featureFlagName as any, target_value)
    }
  }, [isFeatureEnabledHook])

  useEffect(() => {
    const disposeReduxSelectorWarningForwarder = installReduxSelectorWarningForwarder()
    return () => {
      disposeReduxSelectorWarningForwarder?.()
    }
  }, [])

  useEffect(() => {
    window.setStudioMode = function (studioMode) {
      dispatch(setStudioModeAction(studioMode))

      //Note: This only sets the mode, it doesn't update the objects because they haven't actually loaded yet
      if (studioMode === 'hidden') {
        window.editor.setMode('hidden')
      } else {
        var discardChanges = true
        var isInteractive = studioMode === 'studio' || studioMode === 'studioLite' || studioMode === 'explore'
        window.editor.setMode(isInteractive ? 'interactive' : 'presentation', discardChanges)
      }

      window.Designer.onWindowResize(null, true, { immediate: true })
      setStudioMode(studioMode)
    }

    window.launch = function (params, studioMode) {
      if (params && params.api_key_google) {
        window.AccountHelper.overrideApiKeyGoogle = params.api_key_google
      } else {
        // Never clear overrideApiKeyGoogle, only overwrite with a new (not-empty) value
        // to avoid some race-condition bugs
        // window.AccountHelper.overrideApiKeyGoogle = null
      }

      // _this.setState({
      //   studioMode: studioMode,
      // })
      dispatch(setStudioModeAction(studioMode))

      if (studioMode === 'hidden') {
        // exit early for both new and old proUX, skip loading profile and making any other changes when hidden
        return
      }

      window.SceneHelper.showObliqueFirstIfAvailable = false

      if (studioMode === 'project') {
        window.LoaderHelper.active = false
      } else {
        window.LoaderHelper.active = true
      }

      //if metadata missing then assume we are building the design using "init", do not try to parse an existing design
      if (params.data && params.data.metadata) {
        //if self-contained data is available use it
        window.WorkspaceHelper.loadWithData(window.editor, params.data, params)
      } else {
        Sentry.captureException('Error: metadata is missing from design when viewing proposal')
      }

      //For all onWidowResize events pass forceRenderOnFinished=true to force clearing of artifacts

      window.Designer.onWindowResize(null, true)

      //Hack to fix myenergy not resizing as required
      // setTimeout(function(){
      //     if(window.logger) window.logger('window.Designer.onWindowResize() after timeout 10')
      //     window.Designer.onWindowResize()
      // }, 10)

      setTimeout(function () {
        if (window.logger) window.logger('window.Designer.onWindowResize() after timeout 1000')
        window.Designer.onWindowResize(null, true)
      }, 1000)

      //Added as hack when loading MyEnergy after Studio
      setTimeout(function () {
        if (window.logger) window.logger('window.Designer.onWindowResize() after timeout 3000')
        window.Designer.onWindowResize(null, true)
      }, 3000)

      //Call just in case animations were previously stopped on view change
      //window.designerAnimationController('start')

      // Run callbacks when editor is ready. e.g. React elements can fire a setup function after editor is ready
      // Beware: This is a hack for react - ensure the components are not unmounted or re-created otherwise references
      // will be broken.
      if (window.callWhenEditorReady) {
        window.callWhenEditorReady.forEach(function (func) {
          if (func) {
            func()
          } else {
            console.warn('callWhenEditorReady callback function invalid')
          }
        })
        window.callWhenEditorReady = undefined
      }
    }
  }, [])

  return (
    <div id="PersistentContent">
      <AppStateManager />

      <AutoDesignInProgressDialog />
      <SocketInitForChatDrawer />
      <InitSellerIntegrations API_URL={API_URL} />
      <FujiTourController />
      <PvsellProposalDialog />
      <OrderComponentsDialog />
      {studioLoaded && <ProjectPersistentContent />}
      <UnsavedDataRecoveryPrompt />
      {studioLoaded && <InvalidProjectDialog />}
      {studioLoaded && <LoginWithNearmapDialog />}
      <AccessDialog restClientInstance={myApiRestClient(API_URL)} />
      <PromoDialogManager />
      <ListBulkEditGlobalHost />
      <ToastNotificationManager />
      <UploadLogoDialog />
      <CustomImageryDialog />
      {studioLoaded && <DesignerComponent id="DesignerComponent" />}
      <SwitchInterfaceConfirmationDialog />
      <HelpCenterDrawer />
      {showChatDrawerButton && <ChatDrawer />}
      {!isHomePage && (
        <Suspense fallback={null}>
          <SidePanel />
        </Suspense>
      )}
      <SdkMounterIfNested />
      <FloatingAdaIcon />
      {studioLoaded && <DeleteConfirmationDialog />}
      <FeaturesDialog />
    </div>
  )
}

export default PersistentContent
